import { useState } from "react";
import { Link } from "react-router-dom";
import { faqs, companyDetails } from "../data";
import SEO from "../components/SEO";

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "FAQPage",
        "mainEntity": faqs.map((faq) => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        }))
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Forside",
            "item": "https://lederskab.nu/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "FAQ",
            "item": "https://lederskab.nu/faq"
          }
        ]
      }
    ]
  };

  return (
    <div className="bg-white">
      <SEO
        title="Ofte Stillede Spørgsmål (FAQ) | Lederskab.nu"
        description="Få svar på de mest almindelige spørgsmål om lederforløbet, datoer, lokationer, priser og tilmelding hos Lederskab.nu."
        canonicalPath="/faq"
        structuredData={faqSchema}
      />
      {/* Header Banner */}
      <section className="relative bg-gradient-to-br from-maersk-dark to-navy-900 text-white py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(ellipse_at_center,rgba(255,255,255,0.15),transparent_70%)]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-left">
          <div className="max-w-3xl">
            <span className="text-xs font-bold uppercase tracking-widest text-brand-orange bg-brand-orange/20 border border-brand-orange/30 px-3 py-1.5 rounded-full">
              Ofte Stillede Spørgsmål
            </span>
            <h1 className="font-display text-4xl sm:text-5xl font-bold mt-4 mb-6 leading-tight">
              FAQ – Få svar på dine spørgsmål
            </h1>
            <p className="text-gray-300 text-lg sm:text-xl leading-relaxed">
              Her har vi samlet de mest almindelige spørgsmål om vores 12-måneders lederforløb, praktiske forhold, priser og tilmelding.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Accordion Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            {/* Left Column */}
            <div className="space-y-4">
              {faqs.slice(0, Math.ceil(faqs.length / 2)).map((faq, idx) => {
                const originalIndex = idx;
                const isOpen = openIndex === originalIndex;
                return (
                  <div
                    key={originalIndex}
                    className="bg-white rounded-2xl border border-maersk-blue/10 shadow-xs hover:shadow-md transition-all duration-300 overflow-hidden"
                  >
                    <button
                      onClick={() => toggleFAQ(originalIndex)}
                      className="w-full px-6 py-5 sm:px-8 sm:py-6 text-left flex justify-between items-center gap-4 hover:bg-maersk-light/50 transition-colors duration-300 cursor-pointer"
                    >
                      <span className="font-display font-bold text-maersk-dark text-base sm:text-lg">
                        {faq.question}
                      </span>
                      <span className={`w-8 h-8 rounded-full bg-maersk-light flex items-center justify-center text-maersk-blue shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180 bg-maersk-blue text-white" : ""}`}>
                        <i className="fa-solid fa-chevron-down text-xs" />
                      </span>
                    </button>

                    <div
                      className={`transition-all duration-300 ease-in-out overflow-hidden ${
                        isOpen ? "max-h-[500px] border-t border-navy-50" : "max-h-0"
                      }`}
                    >
                      <div className="p-6 sm:p-8 text-left text-navy-700 text-sm sm:text-base leading-relaxed bg-maersk-light/30">
                        {faq.answer}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Right Column */}
            <div className="space-y-4">
              {faqs.slice(Math.ceil(faqs.length / 2)).map((faq, idx) => {
                const originalIndex = Math.ceil(faqs.length / 2) + idx;
                const isOpen = openIndex === originalIndex;
                return (
                  <div
                    key={originalIndex}
                    className="bg-white rounded-2xl border border-maersk-blue/10 shadow-xs hover:shadow-md transition-all duration-300 overflow-hidden"
                  >
                    <button
                      onClick={() => toggleFAQ(originalIndex)}
                      className="w-full px-6 py-5 sm:px-8 sm:py-6 text-left flex justify-between items-center gap-4 hover:bg-maersk-light/50 transition-colors duration-300 cursor-pointer"
                    >
                      <span className="font-display font-bold text-maersk-dark text-base sm:text-lg">
                        {faq.question}
                      </span>
                      <span className={`w-8 h-8 rounded-full bg-maersk-light flex items-center justify-center text-maersk-blue shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180 bg-maersk-blue text-white" : ""}`}>
                        <i className="fa-solid fa-chevron-down text-xs" />
                      </span>
                    </button>

                    <div
                      className={`transition-all duration-300 ease-in-out overflow-hidden ${
                        isOpen ? "max-h-[500px] border-t border-navy-50" : "max-h-0"
                      }`}
                    >
                      <div className="p-6 sm:p-8 text-left text-navy-700 text-sm sm:text-base leading-relaxed bg-maersk-light/30">
                        {faq.answer}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Still Have Questions? */}
      <section className="py-20 bg-navy-50 border-t border-maersk-light">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <div className="w-16 h-16 rounded-full bg-brand-orange-light text-brand-orange flex items-center justify-center text-2xl mx-auto shadow-sm">
            <i className="fa-solid fa-circle-question" />
          </div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-maersk-dark">
            Har du stadig ubesvarede spørgsmål?
          </h2>
          <p className="text-navy-700 max-w-xl mx-auto">
            Hvis du ikke fandt svaret her, eller hvis du gerne vil drøfte, om forløbet er det rette match for dig eller din virksomhed, er du altid velkommen til at kontakte os.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 pt-4">
            <Link
              to="/kontakt"
              className="bg-brand-orange hover:bg-brand-orange-hover text-white px-8 py-4 rounded-md font-bold tracking-wide transition-all duration-300 shadow-md"
            >
              Book en afklarende samtale <i className="fa-solid fa-calendar-check ml-2" />
            </Link>
            <a
              href={`mailto:${companyDetails.email}`}
              className="bg-white hover:bg-maersk-light text-maersk-dark border border-maersk-blue/20 px-8 py-4 rounded-md font-bold tracking-wide transition-all duration-300"
            >
              Send os en e-mail
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
